import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { fadeUp, viewportOnce } from "@/lib/motion";
import { Check, X, RotateCcw, ArrowRight } from "lucide-react";

const QUESTIONS = [
  "Are your policies, forms, intake documents, and compliance files complete and ready to use?",
  "Do you clearly understand which licenses, registrations, and insurance your agency needs before accepting clients?",
  "Are you Medicaid-ready — enrollment, waiver programs, credentialing, billing, and managed care contracting?",
  "Do you have a clear process for recruiting, screening, onboarding, training, and retaining caregivers?",
  "If five clients called today, could you complete intake, assign caregivers, build care plans, and begin care?",
  "Do you have a real marketing and referral strategy for case managers, hospitals, and private-pay families?",
  "Do you have systems for scheduling, EVV, payroll, billing, records, and compliance monitoring?",
  "Do you understand your costs, caregiver pay, reimbursement rates, margins, and working capital needs?",
  "Could you confidently respond today if a regulator requested employee files, client records, or proof of compliance?",
  "Are you ready to operate — not just open — once the license is approved and clients begin calling?",
];

const result = (score) => {
  if (score >= 8)
    return { tier: "Launch Ready", copy: "You may be ready to launch — but test your systems before you scale." };
  if (score >= 5)
    return { tier: "Foundation Built", copy: "You have a foundation, but there are operational gaps to close before real growth." };
  return { tier: "Strategy Needed", copy: "Before launching, you need more than a license — you need strategy, structure, and systems." };
};

export const Readiness = ({ onRegister }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const answered = Object.keys(answers).length;
  const score = Object.values(answers).filter(Boolean).length;
  const pct = (answered / QUESTIONS.length) * 100;

  const set = (i, val) => setAnswers((a) => ({ ...a, [i]: val }));
  const reset = () => { setAnswers({}); setSubmitted(false); };

  return (
    <section id="readiness" data-testid="readiness-section" className="relative border-y border-cream/10 bg-ink-elevated py-20 md:py-32">
      <div className="absolute inset-0 spotlight" aria-hidden="true" />
      <div className="container-tasha relative">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="mx-auto max-w-2xl text-center">
          <span className="overline">Self-Assessment</span>
          <h2 className="mt-5 font-serif text-4xl font-semibold leading-tight tracking-tight text-cream md:text-6xl">
            Are you <span className="font-script text-gold">really</span> ready?
          </h2>
          <p className="mt-6 font-sans text-lg font-light text-cream/70">
            Most people think they're ready. Answer honestly — the ten questions
            below will tell you the truth.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="mx-auto mt-12 max-w-3xl">
          <div className="mb-8 flex items-center gap-4">
            <Progress value={pct} className="h-1.5 bg-cream/10 [&>div]:bg-gold" data-testid="readiness-progress" />
            <span className="flex-none font-sans text-sm text-cream/60">{answered}/{QUESTIONS.length}</span>
          </div>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div key="quiz" exit={{ opacity: 0 }} className="space-y-3">
                {QUESTIONS.map((q, i) => (
                  <div
                    key={i}
                    data-testid={`readiness-q-${i}`}
                    className="flex items-center justify-between gap-4 rounded-xl border border-cream/10 bg-ink/60 p-4 md:p-5"
                  >
                    <p className="font-sans text-sm font-light leading-relaxed text-cream/85 md:text-base">
                      <span className="mr-2 font-serif text-gold">{String(i + 1).padStart(2, "0")}</span>
                      {q}
                    </p>
                    <div className="flex flex-none gap-2">
                      <button
                        onClick={() => set(i, true)}
                        data-testid={`readiness-yes-${i}`}
                        aria-label="Yes"
                        className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all sm:h-10 sm:w-10 md:h-9 md:w-9 ${
                          answers[i] === true ? "border-gold bg-gold text-ink" : "border-cream/20 text-cream/60 hover:border-gold/60"
                        }`}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => set(i, false)}
                        data-testid={`readiness-no-${i}`}
                        aria-label="No"
                        className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all sm:h-10 sm:w-10 md:h-9 md:w-9 ${
                          answers[i] === false ? "border-cream/70 bg-cream/80 text-ink" : "border-cream/20 text-cream/60 hover:border-cream/50"
                        }`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setSubmitted(true)}
                  disabled={answered < QUESTIONS.length}
                  data-testid="readiness-submit-btn"
                  className="btn-gold mt-4 w-full disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {answered < QUESTIONS.length ? `Answer all ${QUESTIONS.length} questions` : "See My Readiness Score"}
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                data-testid="readiness-result"
                className="card-luxe border-gold/30 p-10 text-center"
              >
                <span className="overline">Your Result</span>
                <div className="mt-6 flex items-end justify-center gap-2">
                  <span className="gold-text-gradient font-serif text-8xl font-semibold leading-none">{score}</span>
                  <span className="pb-3 font-serif text-3xl text-cream/40">/ 10</span>
                </div>
                <h3 className="mt-4 font-serif text-3xl font-semibold text-cream md:text-4xl">{result(score).tier}</h3>
                <p className="mx-auto mt-4 max-w-md font-sans text-base font-light text-cream/70">{result(score).copy}</p>
                <p className="mx-auto mt-6 max-w-md font-serif text-xl italic text-cream/80">
                  The real question isn't "Can I open an agency?" — it's "Am I prepared to <span className="text-gold">run</span> one?"
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button onClick={onRegister} className="btn-gold" data-testid="readiness-cta-btn">
                    Get Launch-Ready <ArrowRight size={16} />
                  </button>
                  <button onClick={reset} className="btn-ghost-gold" data-testid="readiness-reset-btn">
                    <RotateCcw size={16} /> Retake
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Readiness;
